import { SaveAttachmentDto } from '@issuemanager/api-client';
import {
  ActionStatusChip,
  CircleStep,
  ReadonlyStack,
  RichTextDisplay,
  ViewLabelValue,
  ViewSection,
  WrappedTypography,
} from '@issuemanager/apps/client/components';
import { useUserDateRenderer } from '@issuemanager/apps/client/hooks';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import { ActionStatus } from '@issuemanager/constants';
import { Box, Button, Stack } from '@mui/material';
import { JSX, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionValues } from '../../manage-issue/action-fields';
import { ActionDetailsApprovalButtons } from '../action-details-approval-buttons';
import { ActionDetailsFinishButton } from '../action-details-finish-button';
import { ActionDetailsStartButton } from '../action-details-start-button';
import ViewIssueUser from '../user/ViewIssueUser';
import { AttachmentsList } from './attachments-list';
import ConfirmReassignmentRequestDialog from './confirm-reassignment-request-dialog';
import useActionsInfo from './hooks/use-action-info';

interface Props {
  actions: ActionValues[];
  executionType: ReactNode;
}

export type FinishActionFormValues = {
  attachments: SaveAttachmentDto[];
};

function ActionDetails({ actions, executionType }: Props): JSX.Element {
  const semantic = useAtlasSemanticThemeTokens();
  const dateRenderer = useUserDateRenderer();
  const { t } = useTranslation('action');
  const actionsInfos = useActionsInfo(actions);

  const [selectedActionId, setSelectedActionId] = useState<
    string | undefined
  >();

  return (
    <>
      <Stack spacing={3} data-testid='actions-list'>
        <Box>{executionType}</Box>

        {actionsInfos.map((action, index) => (
          <ViewSection key={action.id} data-testid='action'>
            <Stack spacing={2}>
              <Stack direction='row' justifyContent='space-between'>
                <Stack direction='row' alignItems='center' spacing={2}>
                  <Box width='24px' height='24px'>
                    <CircleStep fill={semantic.color.action.default.value}>
                      {index + 1}
                    </CircleStep>
                  </Box>
                  <Stack data-testid='title'>
                    <WrappedTypography
                      fontSize={semantic.fontSize.title.md.value}
                      fontWeight={semantic.fontWeight.emphasis.value}
                    >
                      {action.title}
                    </WrappedTypography>
                  </Stack>
                  <ActionStatusChip status={action.status as ActionStatus} />
                </Stack>
                {action.id ? (
                  <Stack direction='row' spacing={2}>
                    {action.canRequestReassignment && (
                      <Button
                        variant='outlined'
                        onClick={() => setSelectedActionId(action.id)}
                        data-testid='request-reassignment-button'
                      >
                        {t('actions.requestReassignment')}
                      </Button>
                    )}
                    {action.canStartAction && (
                      <ActionDetailsStartButton id={action.id} />
                    )}
                    {action.title && action.canFinishAction && (
                      <ActionDetailsFinishButton
                        id={action.id}
                        title={action.title}
                      />
                    )}
                    {action.title && action.canApproveAction && (
                      <ActionDetailsApprovalButtons
                        id={action.id}
                        title={action.title}
                      />
                    )}
                  </Stack>
                ) : null}
              </Stack>

              <ReadonlyStack>
                <Box flex={1}>
                  <Stack spacing={3}>
                    <ViewLabelValue
                      label={t('view.details.description')}
                      value={
                        <RichTextDisplay
                          text={action.description ?? ''}
                          maxLines={3}
                          testId='description'
                        />
                      }
                    />
                    <AttachmentsList attachments={action.attachments ?? []} />
                  </Stack>
                  {/*
                NOTE: related entities will be used in the future so far are not needed.
                Ticket RCP-12663
                <Grid item xs={12}>
                  <ViewLabelValue
                    label={t('view.details.entities')}
                    value={action.govmapGroups?.map((item, index) => (
                      <GovmapGroupsListItem
                        key={item.id}
                        name={item.name}
                        parentName={item.parentGroupName}
                        showSeparator={
                          index !== (action.govmapGroups?.length || 0) - 1
                        }
                      />
                    ))}
                  />
                </Grid>
                */}
                </Box>
                <Box flex={1}>
                  <Stack spacing={3}>
                    <ViewLabelValue
                      label={t('view.details.dueDate')}
                      value={
                        <span data-testid='due-date'>
                          {dateRenderer.renderDateTime(action.dueDate)}
                        </span>
                      }
                    />
                    <ViewIssueUser
                      labelText={t('view.details.owner')}
                      name={action.owner?.name ?? '-'}
                      testId='owner'
                    />
                    <Stack spacing={2}>
                      <Box>
                        <Stack
                          direction='row'
                          justifyContent='space-between'
                          spacing={1}
                        >
                          <ViewIssueUser
                            labelText={t('view.details.approver')}
                            name={action.approver?.name ?? '-'}
                            testId='approver'
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Stack>
                </Box>
              </ReadonlyStack>
            </Stack>
          </ViewSection>
        ))}
      </Stack>
      {selectedActionId && (
        <ConfirmReassignmentRequestDialog
          actionId={selectedActionId}
          open={Boolean(selectedActionId)}
          onClose={() => setSelectedActionId(undefined)}
        />
      )}
    </>
  );
}

export default ActionDetails;
